// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import CycleGraphs from "./CycleGraphs"
import Leaderboard from "./Leaderboard"
import EventsPanel from "./EventsPanel"
import CycleHeadStrip from "./CycleHeadStrip"
import PhaseNarration from "./PhaseNarration"
import ReferenceBlock from "./ReferenceBlock"

/**
 * Cycle dashboard shell — iso with
 * `ebiose-webapp/src/views/cycles/details.tsx`.
 *
 * Top row (mockup `cycle-row-2a`, issue `cycle-head-strip`): the single
 * full-width `CycleHeadStrip` — the merged AGENTS cluster + four INFERENCE
 * metrics (INV-11). Then the tabbed
 * CycleGraphs (fitness / cost / agentCost), the Candidate leaderboard, and
 * EventsPanel. The former standalone `CycleOverview` metric-tiles block
 * (created / run / failed / llm) was retired: `candidates` now heads the
 * Candidate leaderboard and the spend/reliability signals live in the strip.
 *
 * EventsPanel stays mounted below regardless, so a broken meta agent or a
 * stall is still visible from the relevant-events feed even before any
 * snapshot.
 *
 * The cycle header band (forge name + status + engine + live/finished
 * indicator, mockup "2A") lives one level up in the details view
 * (`details.tsx`), not here — the shell no longer renders its own
 * header, so the identity/liveness band shows regardless of whether a
 * snapshot ever landed.
 *
 * Per-panel gating (issue `per-panel-gating-t0`, PRD §Dashboard gating
 * contract): the shell mounts every panel unconditionally and each one
 * gates on *its own* data source. The detail-DTO-fed `CycleHeadStrip`
 * renders at t=0 from the persisted cycle row; snapshot-fed panels
 * (CycleGraphs / Leaderboard / ReferenceBlock) render their own zero-states
 * until the first `EvolutionCycleSnapshotEvent` lands. No single flag can
 * blank the whole dashboard, so a healthy pre-snapshot cycle never shows a
 * global error.
 *
 * US §12 read-only invariant — no mutation buttons anywhere.
 */
export interface CycleDashboardProps {
  cycleId: string
}

export default function CycleDashboard({ cycleId }: CycleDashboardProps) {
  return (
    <section
      aria-label="Cycle dashboard"
      className="flex flex-col gap-6"
      data-cycle-id={cycleId}
    >
      <PhaseNarration cycleId={cycleId} />
      <CycleHeadStrip cycleId={cycleId} />
      <div className="flex flex-col gap-6 xl:flex-row xl:items-stretch">
        <CycleGraphs cycleId={cycleId} />
        <ReferenceBlock cycleId={cycleId} />
      </div>
      <Leaderboard cycleId={cycleId} />

      <EventsPanel cycleId={cycleId} />
    </section>
  )
}
